import React, {useEffect, useRef, useState} from 'react';
import {PtrnAccionPametroAuxiliar, PtrnCampoEditableArray, PtrnColors} from '../../../patronTypes';
import {EditableProps} from '../campoEditableTypes';
import {getNoBorderSx, useInputStyles} from '../campoEditableUtils';
import {PtrnPropsUtil} from '../../../patronUtils';
import {FilterOptionsState, Paper, Popper} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import PtrnAccionButton from '../../PtrnAccionButton';

function CampoEditableSelect(
   props: {campoConfig: PtrnCampoEditableArray} & EditableProps & {
         ErrorComponent?: React.ReactNode;
      }
) {
   const {campoConfig, ...editableProps} = props;
   const classes = useInputStyles();
   const [esEditable, setEsEditable] = useState(false);
   const [inputValue, setInputValue] = useState('');
   const [openList, setOpenList] = useState(false);
   //Este estado es la marca de un item que es apuntado en la lista del auto complete pero no seleccionado
   const higthLightItemRef = useRef<any>(null);
   const autocompleteRef = useRef(null);

   const auxParam: PtrnAccionPametroAuxiliar = {
      camposData: editableProps.camposData,
      campoConfig: campoConfig,
      ptrnRef: editableProps.ptrnRef,
      ruta: editableProps.ruta,
      index: editableProps.index
   };

   //#region ExternalFilter
   const externalFilter = () => {
      if (campoConfig.array) {
         if (campoConfig.externalFilter) {
            return campoConfig.array.filter((value) => campoConfig.externalFilter(value, auxParam));
         } else {
            return campoConfig.array;
         }
      } else {
         return [];
      }
   };
   //#endregion ExternalFilter
   //#region labelItem y labelSelected
   const labelItem = (campoValor: any): string => {
      if (campoConfig.generarDenominacionMostrar) {
         return campoConfig.generarDenominacionMostrar(campoValor, auxParam);
      }

      // Asegurarte de que sea un string o renderizable
      if (typeof campoValor === 'object') {
         return JSON.stringify(campoValor); // Convierte el objeto a string
      }
      return campoValor.toString(); // Convierte otros valores a string
   };

   const labelItemSelected = (campoValor: any): string => {
      if (campoValor && campoValor != null) {
         if (campoConfig.generarValorMostrar) {
            return campoConfig.generarValorMostrar(campoValor, auxParam);
         }
         // Asegurarte de que sea un string o renderizable
         if (typeof campoValor === 'object') {
            return JSON.stringify(campoValor); // Convierte el objeto a string
         }
         return campoValor.toString(); // Convierte otros valores a string
      } else {
         return '';
      }
   };
   //#endregion labelItem y labelSelected
   //#region handles
   const handleOnChange: (event: React.SyntheticEvent, value: unknown) => void = (
      _event,
      newValue
   ) => {
      const name = campoConfig.name;
      editableProps.onChange(name, newValue, auxParam);
   };

   const handleOnBlur: React.FocusEventHandler<HTMLDivElement> = (e) => {
      if (editableProps.onBlur) {
         const name = campoConfig.name;
         let newValue = editableProps.value;
         editableProps.onBlur(name, newValue, auxParam);
      }
      setEsEditable(false);
      if (!editableProps.value || editableProps.value === null) {
         setInputValue('');
      }
   };

   const handleOnFocus: React.FocusEventHandler<HTMLInputElement> = (e) => {
      setEsEditable(true);
      setTimeout(() => e.target.select(), 0);
      setOpenList(true);
   };

   //#endregion handles

   //#region control de asignacion
   const isOptionEqualToValue = (option: unknown, value: unknown) => {
      if (campoConfig.isOptionEqualToValue) {
         return campoConfig.isOptionEqualToValue(option, value);
      } else {
         return labelItem(option) === labelItem(value);
      }
   };
   //#endregion control de asignacion
   //#region control filtrador
   const filterOptionsAdapter = (options: unknown[], state: FilterOptionsState<unknown>) => {
      const inputValue = state.inputValue.toLocaleLowerCase();
      if (!inputValue) {
         return options;
      }
      const naturalSearch = (option: unknown, input: string) => {
         return labelItem(option).toLowerCase().includes(input);
      };
      if (campoConfig.prioridades && campoConfig.prioridades.length > 0) {
         return options
            .map((option) => {
               const valoresPrioritarios = campoConfig.prioridades.map(
                  (prioridad) => option[prioridad]?.toString().toLowerCase() || ''
               );

               const coincidencias = valoresPrioritarios.filter((valor) =>
                  valor.includes(inputValue)
               );
               if (coincidencias.length === 0) return null;

               // Determinar prioridad basado en la posición de la coincidencia
               const prioridadMin = Math.min(
                  ...coincidencias.map((valor) => valor.indexOf(inputValue))
               );

               return {option, prioridad: prioridadMin};
            })
            .filter(Boolean) // Eliminar valores nulos (sin coincidencias)
            .sort((a, b) => a.prioridad - b.prioridad) // Ordenar por mejor coincidencia
            .map((item) => item.option);
      } else {
         if (campoConfig.filterOptions)
            return options.filter((option) =>
               campoConfig.filterOptions!(option, state.inputValue, naturalSearch)
            );
      }
      return options.filter((option) => naturalSearch(option, inputValue));
   };
   //#endregion control filtrador
   //#region Asignacion por defecto
   // generalemente usado para cargar datos iniciales
   const defaultValueFilter = () => {
      if (campoConfig.defaultValueFilter && editableProps.value) {
         const value = campoConfig.array!.find((option) =>
            campoConfig.defaultValueFilter(option, editableProps.value)
         );
         if (value) {
            editableProps.onChange(campoConfig.name, value, auxParam);
            setInputValue(labelItem(value));
            setOpenList(false);
         }
      }
      if (!editableProps.value || editableProps.value === null) {
         setInputValue('');
      }
   };
   //#endregion Asignacion por defecto
   //#region GEN PROPS
   const id = PtrnPropsUtil.genIdCampoEditable(campoConfig);
   const endAdornments = props.ErrorComponent ? (
      <InputAdornment position="end">{props.ErrorComponent}</InputAdornment>
   ) : undefined;
   //#endregion

   const InputSearch = (params: any) => {
      const {InputProps, inputProps, ...restParams} = params;
      // Modificamos el valor mostrado en el input para usar labelItemSelected
      const modifiedInputProps = {
         ...inputProps,
         value: esEditable ? inputValue : labelItemSelected(editableProps.value)
      };
      return (
         <TextField
            {...restParams}
            InputProps={{
               ...InputProps,
               endAdornment: (
                  <>
                     {InputProps.endAdornment}
                     {endAdornments}
                  </>
               )
            }}
            inputProps={modifiedInputProps}
            label={campoConfig.label}
            error={Boolean(props.ErrorComponent)}
            size="small"
         />
      );
   };

   useEffect(() => {
      defaultValueFilter();
   }, []);

   useEffect(() => {
      defaultValueFilter();
   }, [editableProps.value,campoConfig.array]);//TODO: revisar si es necesario el array

   const filteredOptions = externalFilter();
   useEffect(() => {
      if (
         campoConfig.asignarPrimerValorSiNull &&
         editableProps.value === null &&
         filteredOptions.length > 0 &&
         !esEditable
      ) {
         editableProps.onChange(campoConfig.name, filteredOptions[0], auxParam);
      }
   }, [editableProps.value, campoConfig.array, esEditable]);

   //#region RETORNOS
   return (
      <FormControl fullWidth error={Boolean(props.ErrorComponent)}>
         <Autocomplete
            id={id}
            ref={autocompleteRef}
            sx={getNoBorderSx(campoConfig)}
            options={filteredOptions}
            getOptionLabel={labelItem}
            value={editableProps.value}
            open={openList}
            onOpen={() => setOpenList(true)}
            onClose={() => setOpenList(false)}
            onChange={handleOnChange}
            onBlur={handleOnBlur}
            onFocus={handleOnFocus}
            renderInput={InputSearch}
            autoHighlight={true}
            onHighlightChange={(event, option, reason) => {
               higthLightItemRef.current = option;
            }}
            onKeyDown={(event) => {
               if (event.key === 'Tab' && event.shiftKey) {
                  editableProps.onChange(campoConfig.name, higthLightItemRef.current, auxParam);
               } else if (event.key === 'Tab') {
                  editableProps.onChange(campoConfig.name, higthLightItemRef.current, auxParam);
               }
            }}
            // autoSelect={true}
            isOptionEqualToValue={isOptionEqualToValue}
            renderOption={(props, option) => (
               <li
                  {...props}
                  style={
                     campoConfig.activeModeEllipsis
                        ? {
                             overflow: 'hidden',
                             textOverflow: 'ellipsis',
                             whiteSpace: 'nowrap',
                             display: 'block', // Necesario para que funcione bien con ellipsis
                             width: '100%' // Asegura que el texto se limite al ancho del popper
                          }
                        : undefined
                  }
                  data-value={JSON.stringify(option)}
               >
                  {labelItem(option)}
               </li>
            )}
            noOptionsText={
               <div>
                  {campoConfig.emptyAction ? (
                     <PtrnAccionButton
                        name={id + '-empty-button'}
                        accion={{
                           fn: () => {
                              campoConfig.emptyAction(inputValue, auxParam);
                              setOpenList(false);
                           },
                           label: campoConfig.labelEmptyAction
                              ? campoConfig.labelEmptyAction(inputValue)
                              : 'Guardar'
                        }}
                        fullWidth
                        color={PtrnColors.secondary}
                     />
                  ) : (
                     campoConfig.emptyMessage || 'No hay opciones disponibles'
                  )}
               </div>
            }
            filterOptions={filterOptionsAdapter}
            disableClearable={campoConfig.disableClearable}
            inputValue={esEditable ? inputValue : ''}
            onInputChange={(event, newInputValue) => {
               if (esEditable) {
                  setInputValue(newInputValue);
               }
            }}
            PaperComponent={({children, ...props}) => (
               <Paper
                  {...props}
                  sx={{
                     width: 'auto',
                     minWidth: '100%', // Asegura el ancho mínimo igual al ancho del autocomplete
                     maxWidth: 'none', // Permite que crezca si es necesario
                     alignSelf: 'center'
                  }}
               >
                  {children}
               </Paper>
            )}
            PopperComponent={({style, ...props}) => (
               <Popper
                  {...props}
                  style={{
                     ...style,
                     position: 'absolute', //Permite que el componente se alinie al componente padre
                     width: campoConfig.popperWidth ?? '0',
                     minWidth: style?.width, // Mantiene el ancho del input como mínimo
                     zIndex: 1300 // Ajusta según necesites
                  }}
                  placement={campoConfig.popperAlignRight ? 'bottom-end' : 'bottom-start'}
               />
            )}
         />
      </FormControl>
   );
   //#endregion
}

export default CampoEditableSelect;
